import React from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

interface Props {
  /** Whether the dialog is visible */
  open: boolean;
  /** Called when the agent dismisses the prompt without switching */
  onClose: () => void;
  /** Called when the agent confirms the switch */
  onConfirm: () => void;
  /** Display name of the account's current region, e.g. "Australia" */
  currentRegionLabel: string;
  /** Display name of the region being switched to, e.g. "New Zealand" */
  newRegionLabel: string;
  /** Whether the switch request is currently in flight */
  isSubmitting: boolean;
}

/**
 * Confirmation prompt shown before an agent changes a Société account's region.
 *
 * The account region is compared against the order's country in two places, and neither
 * surfaces an error to the agent when they disagree:
 * - at checkout, a mismatch produces no pending points for the order
 * - at accrual, an item with no pending points is closed as PROCESSED and never reprocessed
 *
 * Because both failures are silent, the consequences are spelled out here instead. The caller
 * owns the mutation; this component only renders the prompt.
 */
export default function RegionSwitchConfirmDialog({
  open,
  onClose,
  onConfirm,
  currentRegionLabel,
  newRegionLabel,
  isSubmitting,
}: Props) {
  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm region switch</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Typography component="p">
            Switch this Société account from <strong>{currentRegionLabel}</strong> to <strong>{newRegionLabel}</strong>?
          </Typography>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="body2" component="p">
                <strong>Already-accrued orders are unaffected.</strong> Points, status credits and nights already
                awarded stay awarded.
              </Typography>
              <Typography variant="body2" component="p">
                <strong>Orders still to travel, booked on the {currentRegionLabel} site:</strong> these still earn. They
                already have pending points, and accrual doesn&apos;t re-check the region once pending points exist.
              </Typography>
              <Typography variant="body2" component="p">
                <strong>Orders still to travel, booked on the {newRegionLabel} site:</strong> these can earn again once
                the region matches. Pending points are <strong>not</strong> rebuilt automatically, so accrual falls back
                to its own estimate — anything over 5,000 points goes to manual review rather than paying out.
              </Typography>
              <Typography variant="body2" component="p">
                <strong>Timing matters.</strong> Only orders that haven&apos;t reached accrual yet are affected. Once an
                item has been through accrual it is never reprocessed, so switching back later cannot recover points
                that were already missed — those need a manual adjustment.
              </Typography>
            </Stack>
          </Alert>

          <Typography variant="body2" component="p" color="text.secondary" sx={{ mt: 2 }}>
            This takes effect immediately.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onConfirm}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSubmitting ? 'Switching…' : 'Confirm switch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
